//frontend//src//components//Mensalidades//Mensalidades.js
import React, { useState, useEffect, useRef } from 'react';
import io from "socket.io-client";
import { FaSearch, FaFilter, FaFileExport, FaPlus, FaChartBar } from 'react-icons/fa';
import Modal from 'react-modal';
import styles from './Mensalidades.module.css';
import DetalhesModal from './DetalhesModal';
import PagamentoMensalModal from './PagamentoMensal';
import RelatorioMensalidadesModal from './RelatorioMensalidadesModal';
import { exportarMensalidades } from '../../utils/exportarMensalidades'; 
import ReciboMensalidades, { gerarReciboMensalidade } from './ReciboMensalidades';

const parseAmount = (valor) => {
  if (!valor) return 0;
  if (typeof valor === 'number') return valor;
  if (valor.includes(',')) {
    const limpo = valor.replace(/\./g, '').replace(',', '.');
    return parseFloat(limpo) || 0;
  }
  return parseFloat(valor) || 0;
};

const Mensalidades = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mensalidades, setMensalidades] = useState([]);
  const [filters, setFilters] = useState({ year: 'all', month: 'all', class: 'all', status: 'all' });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [selectedMensalidade, setSelectedMensalidade] = useState(null);
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [pagamentoModalOpen, setPagamentoModalOpen] = useState(false);
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);
  const [reciboModalOpen, setReciboModalOpen] = useState(false);
  const exportRef = useRef(null);

  // 🔹 Fetch inicial das mensalidades
useEffect(() => {
  const fetchMensalidades = async () => {
    try {
      const res = await fetch("/api/mensalidades")
      if (!res.ok) throw new Error("Erro ao buscar mensalidades");
      const data = await res.json();

      const mensalidadesNormalizadas = (data.mensalidades || []).map(m => ({
  id: m.id,
  referencia: m.referencia,

  codigo_aluno: m.aluno?.codigo_aluno || "N/A",
  nome_completo: m.aluno?.nome_completo || "Desconhecido",
  classe: m.aluno?.classe || "N/A",
  turma: m.aluno?.turma || "N/A",

  // 🔹 DADOS PARA FILTRO
  mes: m.mes,
  ano_lectivo: m.ano_lectivo,

  // 🔹 APENAS EXIBIÇÃO
  mesAnoExibicao: m.mesAno,

  valor_base: parseAmount(m.valor),
  valor_multa: parseAmount(m.multa),
  total: parseAmount(m.total),
  valor_pago: parseAmount(m.pago || 0),
  saldo: parseAmount(m.saldo),
  credito: parseAmount(m.credito),
  estado: m.estado || "PENDENTE",
  entidade: m.entidade || "________",
  forma_pagamento: m.forma_pagamento || "PENDENTE",
  detalhes: m.detalhes,
}));


      setMensalidades(mensalidadesNormalizadas);
    } catch (error) {
      console.error("Erro ao carregar mensalidades:", error);
    }
  };

  fetchMensalidades();
}, []);

  // 🔹 Socket.io para atualização em tempo real
  useEffect(() => {
    const socket = io();

    socket.on("mensalidade_atualizada", (mensalidadeAtualizada) => {
  setMensalidades(prev =>
    prev.map(m => {
      if (m.id === mensalidadeAtualizada.id) {
        const total = Number(mensalidadeAtualizada.valor_total ?? m.total);
        const pago = Number(mensalidadeAtualizada.valor_pago ?? m.valor_pago);

        return {
          ...m,
          total,
          valor_pago: pago,
          saldo: Math.max(total - pago, 0),
          estado: mensalidadeAtualizada.estado,
          data_pagamento: mensalidadeAtualizada.data_pagamento,
        };
      }
      return m;
    })
  );
});

    return () => socket.disconnect();
  }, []);

  // 🔹 Fechar dropdown export quando clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportOptions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔹 Filtragem das mensalidades
  const filteredMensalidades = mensalidades.filter(m => {
    const matchesSearch =
      (m.nome_completo || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.codigo_aluno || '').includes(searchQuery);
    const matchesYear = filters.year === 'all' || m.ano_lectivo === Number(filters.year);
    const matchesMonth = filters.month === 'all' || m.mes === Number(filters.month);
    const matchesClass = filters.class === 'all' || m.classe === filters.class;
    const matchesStatus = filters.status === 'all' || m.estado?.toUpperCase() === filters.status.toUpperCase();
    return matchesSearch && matchesYear && matchesMonth && matchesClass && matchesStatus;
  });

  const handleExport = (format) => {
    if (!filteredMensalidades.length) {
      alert("Não há dados para exportar.");
      return;
    }
    exportarMensalidades(filteredMensalidades, format);
  };

  const traduzStatus = (s) => ({
  PAGO: 'Pago',
  PENDENTE: 'Pendente',
  PARCIAL: 'Parcial'
}[s?.toUpperCase()] || s);

const formatarData = (data) => {
  if (!data) return "—";
  const d = new Date(data);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-PT");
};

  const fmtValor = v => parseAmount(v).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={styles.mensalidadesContainer}>
      <div className={styles.header}>
        <h2>Gestão de Mensalidades</h2>
        <div className={styles.actions}>
          <div className={styles.searchBar}>
            <FaSearch />
            <input 
              placeholder="Pesquisar aluno ou código..." 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
            />
          </div>

          <button className={styles.filterBtn} onClick={() => setShowFilterModal(true)}>
            <FaFilter /> Filtrar
          </button>

          <div className={styles.dropdownExport} ref={exportRef}>
            <button className={styles.exportBtn} onClick={() => setShowExportOptions(prev => !prev)}>
              <FaFileExport /> Exportar
            </button>
            {showExportOptions && (
              <div className={styles.exportOptions}>
                <button onClick={() => handleExport('pdf')}>PDF</button>
                <button onClick={() => handleExport('excel')}>Excel</button>
                <button onClick={() => handleExport('csv')}>CSV</button>
              </div>
            )}
          </div>

          <button className={styles.btn} onClick={() => setPagamentoModalOpen(true)}><FaPlus /> Pagamentos</button>
          <button className={styles.btn} onClick={() => setRelatorioModalOpen(true)}><FaChartBar /> Relatórios</button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Código</th>
              <th>Aluno</th>
              <th>Referência</th>
              <th>Entidade</th>
              <th>Mês/Ano</th>
              <th>Valor</th>
              <th>Multa</th>
              <th>Saldo</th>
              <th>Crédito</th>
              <th>Pago</th>
              <th>Status</th>
              <th>Forma Pagamento</th>
              <th>Detalhes</th>
           
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {!filteredMensalidades.length && (
              <tr><td colSpan="12" className={styles.centerMsg}>Nenhuma mensalidade encontrada.</td></tr>
            )}
            {filteredMensalidades.map(m => (
              <tr key={m.id}>
                <td>{m.codigo_aluno}</td>
                <td>{m.nome_completo}</td>
                <td>{m.referencia}</td>
                <td>{m.entidade}</td>
                <td>{m.mesAnoExibicao}</td>
                <td>{fmtValor(m.valor_base)} MZN</td>
                <td>{fmtValor(m.valor_multa)} MZN</td>
                <td>{fmtValor(m.saldo)} MZN</td>
                <td>{fmtValor(m.credito)} MZN</td>
                <td>{fmtValor(m.valor_pago)} MZN</td>
                <td>{traduzStatus(m.estado)}</td>
                <td> {m.forma_pagamento === "PENDENTE"
                      ? "Pendente"
                       : m.forma_pagamento} </td>
                <td>
                 {m.detalhes
                    ? m.detalhes.telefone
                    ? `${formatarData(m.detalhes.data)} | ${m.detalhes.telefone}`
                      : formatarData(m.detalhes.data) : "—"}
                </td>
                <td>
                  <button className={styles.btn} onClick={() => gerarReciboMensalidade(m)}>Recibo</button>
                  <button className={styles.btn} onClick={() => { setSelectedMensalidade(m); setDetalhesModalOpen(true); }}>Detalhes</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de filtro */}
      <Modal
        isOpen={showFilterModal}
        onRequestClose={() => setShowFilterModal(false)}
        className={styles.filterModal}
        overlayClassName={styles.modalOverlay}
      >
        <h2>Filtrar Mensalidades</h2>
        <div className={styles.modalContent}>
          <label>
            Ano Letivo
            <select value={filters.year} onChange={e => setFilters({ ...filters, year: e.target.value })}>
              <option value="all">Todos</option>
              {[2023,2024,2025,2026,2027,2028,2029,2030].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>

          <label>
            Mês
            <select value={filters.month} onChange={e => setFilters({ ...filters, month: e.target.value })}>
              <option value="all">Todos</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('pt-PT', { month: 'long' })}</option>
              ))}
            </select>
          </label>

          <label>
            Classe
            <select value={filters.class} onChange={e => setFilters({ ...filters, class: e.target.value })}>
              <option value="all">Todas</option>
              {['1ª Classe','2ª Classe','3ª Classe','4ª Classe','5ª Classe','6ª Classe','7ª Classe','8ª Classe','9ª Classe','10ª Classe','11ª Classe','12ª Classe'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>

          <label>
            Status
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
              <option value="all">Todos</option>
              <option value="PENDENTE">Pendente</option>
              <option value="PAGO">Pago</option>
              <option value="PARCIAL">Parcial</option>
            </select>
          </label>
        </div>

        <div className={styles.buttonsContainer}>
          <button className={styles.applyFilter} onClick={() => setShowFilterModal(false)}>Aplicar</button>
          <button className={styles.cancelButton} onClick={() => setShowFilterModal(false)}>Fechar</button>
        </div>
      </Modal>

      {/* Modais */}
      {detalhesModalOpen && selectedMensalidade && (
        <DetalhesModal
          isOpen={detalhesModalOpen}
          onClose={() => setDetalhesModalOpen(false)}
          mensalidade={selectedMensalidade}
        />
      )}

<PagamentoMensalModal
  isOpen={pagamentoModalOpen}
  onClose={() => setPagamentoModalOpen(false)}
  mensalidade={selectedMensalidade}
  onSave={(referencia) => {
    // 🔹 Atualiza a tabela localmente
    const fetchAtualizada = async () => {
      if (!referencia) return;
      try {
        const res = await fetch(`/api/mensalidades/referencia/${referencia}`);
        if (!res.ok) return;
        const data = await res.json();

       setMensalidades(prev =>
  prev.map(m =>
    m.referencia === referencia
      ? {
          ...m,
          forma_pagamento: data.forma_pagamento || m.forma_pagamento,
          detalhes: data.detalhes || m.detalhes,
          estado: data.estado || m.estado,
          valor_pago: data.valor_pago ?? m.valor_pago,
          saldo: data.saldo ?? m.saldo,
          total: data.total ?? m.total,
          credito: data.credito ?? m.credito,
          multa: data.multa ?? m.multa,
        }
      : m
  )
);
      } catch (err) {
        console.error("Erro ao atualizar mensalidade:", err);
      }
    };
    fetchAtualizada();
  }}
/>


      {relatorioModalOpen && (
        <RelatorioMensalidadesModal
          isOpen={relatorioModalOpen}
          onClose={() => setRelatorioModalOpen(false)}
          mensalidades={filteredMensalidades}
        />
      )}

      {reciboModalOpen && selectedMensalidade && (
        <ReciboMensalidades
          isOpen={reciboModalOpen}
          onClose={() => setReciboModalOpen(false)}
          pagamento={selectedMensalidade}
        />
      )}
    </div>
  );
};

export default Mensalidades;
